import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X, Clapperboard, Popcorn } from "lucide-react";
import { fetchVideos, resolveImage } from "@/lib/api";

const META = {
  clip: {
    title: "1-Minute Clips",
    sub: "Quick songs and giggles — perfect for little attention spans.",
    icon: Clapperboard,
    color: "#0284C7",
  },
  episode: {
    title: "10-Minute Episodes",
    sub: "Settle in for a full adventure with the whole crew.",
    icon: Popcorn,
    color: "#9333EA",
  },
};

const Watch = ({ kind }) => {
  const meta = META[kind];
  const [videos, setVideos] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    setActive(null);
    fetchVideos(kind).then(setVideos).catch(() => {});
  }, [kind]);

  return (
    <div className="min-h-screen bg-[#FFFDF6] pb-24 pt-28" data-testid={`watch-${kind}-page`}>
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <Link to="/" className="text-sm font-bold text-slate-400 transition-colors hover:text-slate-700" data-testid="watch-back-link">
          &larr; Back to home
        </Link>
        <div className="mt-6 flex items-center gap-3">
          <span className="grid h-12 w-12 place-items-center rounded-2xl" style={{ backgroundColor: `${meta.color}22` }}>
            <meta.icon className="h-6 w-6" style={{ color: meta.color }} />
          </span>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{meta.title}</h1>
            <p className="text-sm text-slate-500">{meta.sub}</p>
          </div>
        </div>

        {videos.length === 0 ? (
          <div className="mt-12 rounded-3xl border border-dashed border-[#E2DDD0] bg-white p-16 text-center" data-testid={`watch-${kind}-empty`}>
            <meta.icon className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-4 font-display text-lg font-semibold text-slate-700">Nothing here yet</p>
            <p className="mt-1 text-sm text-slate-400">
              New {kind === "clip" ? "clips" : "episodes"} are on their way — check back soon.
            </p>
          </div>
        ) : (
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {videos.map((v, i) => (
              <motion.button
                key={v.video_id}
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                whileHover={{ y: -6 }}
                onClick={() => setActive(v)}
                data-testid={`video-card-${v.video_id}`}
                className="group overflow-hidden rounded-3xl border border-[#E2DDD0] bg-white text-left shadow-[0_10px_30px_-5px_rgba(234,179,8,0.1)] transition-shadow hover:shadow-[0_20px_40px_-10px_rgba(14,165,233,0.2)]"
              >
                <div
                  className="relative grid h-44 place-items-center"
                  style={{ background: `linear-gradient(135deg, ${meta.color}18, ${meta.color}44)` }}
                >
                  <span
                    className="grid h-16 w-16 place-items-center rounded-full text-white shadow-xl transition-transform group-hover:scale-110"
                    style={{ backgroundColor: meta.color }}
                  >
                    <Play className="ml-1 h-7 w-7 fill-white" />
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-slate-900/80 px-3 py-1 text-xs font-bold text-white">
                    {v.duration_label}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-bold text-slate-900">{v.title}</h3>
                  <p className="mt-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                    {kind === "clip" ? "Quick clip" : "Full episode"}
                  </p>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] grid place-items-center bg-slate-900/70 p-4 backdrop-blur-sm"
            onClick={() => setActive(null)}
            data-testid="video-modal-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, y: 30, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl overflow-hidden rounded-3xl bg-slate-900 shadow-2xl"
              data-testid="video-modal"
            >
              <button
                onClick={() => setActive(null)}
                data-testid="video-modal-close"
                aria-label="Close video"
                className="absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full bg-black/60 text-white transition-transform hover:scale-110"
              >
                <X className="h-4 w-4" />
              </button>
              <video
                src={resolveImage(active.video_url)}
                controls
                autoPlay
                playsInline
                className="w-full bg-black"
                data-testid="video-player"
              />
              <p className="px-5 py-4 font-display text-base font-semibold text-white">{active.title}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Watch;
